import { useState } from 'react';
import { MdClose, MdUpload, MdDelete } from 'react-icons/md';
import toast from 'react-hot-toast';
import { useCreateTrainingPost } from '../../hooks/useTrainingJournal';
import DateField from '../common/DateField';

function CreatePostModal({ isOpen, onClose, onSuccess }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [postDate, setPostDate] = useState(new Date().toISOString().split('T')[0]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [errors, setErrors] = useState({});

  const { mutate: createPost, isPending } = useCreateTrainingPost();

  const handleAddTag = () => {
    if (tagInput.trim() && tags.length < 5) {
      const newTag = tagInput.trim().toLowerCase();
      if (!tags.includes(newTag)) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handlePhotoSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (photos.length + selectedFiles.length > 10) {
      toast.error('Maximum 10 photos per post');
      return;
    }

    setPhotos([...photos, ...selectedFiles]);

    // Generate previews
    selectedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreviews((prev) => [...prev, event.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = (idx) => {
    setPhotos(photos.filter((_, i) => i !== idx));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!title || title.trim().length < 2) newErrors.title = 'Title required (2+ chars)';
    if (!content || content.trim().length < 5) newErrors.content = 'Content required (5+ chars)';
    if (!postDate) newErrors.postDate = 'Post date required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      title: title.trim(),
      content: content.trim(),
      postDate,
      tags,
      photos,
    };

    createPost(payload, {
      onSuccess: () => {
        toast.success('Training post created!');
        handleClose();
        if (onSuccess) onSuccess();
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Failed to create post');
      },
    });
  };

  const handleClose = () => {
    if (isPending) return;
    setTitle('');
    setContent('');
    setPostDate(new Date().toISOString().split('T')[0]);
    setTags([]);
    setTagInput('');
    setPhotos([]);
    setPhotoPreviews([]);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const labelCls = 'block text-xs font-semibold text-muted mb-1.5';
  const inputCls = (err) =>
    `w-full bg-card border rounded-lg px-3 py-2 text-sm text-secondary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors ${
      err ? 'border-red-500' : 'border-divider'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Sticky header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary text-base">Create Training Post</h2>
          <button
            onClick={handleClose}
            disabled={isPending}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover transition-colors disabled:opacity-50"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Form: scrollable body + sticky footer */}
        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            {/* Title */}
            <div>
              <label className={labelCls}>Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={isPending}
                placeholder="e.g. Advanced Sparring Session"
                maxLength={100}
                className={inputCls(errors.title)}
              />
              <div className="flex justify-between mt-1">
                {errors.title ? <p className="text-xs text-red-500">{errors.title}</p> : <span />}
                <p className="text-xs text-subtle">{title.length}/100</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <label className={labelCls}>Content *</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isPending}
                placeholder="What happened in today's training…"
                maxLength={2000}
                rows={4}
                className={`${inputCls(errors.content)} resize-none`}
              />
              <div className="flex justify-between mt-1">
                {errors.content ? <p className="text-xs text-red-500">{errors.content}</p> : <span />}
                <p className="text-xs text-subtle">{content.length}/2000</p>
              </div>
            </div>

            {/* Post Date + Tags — side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Post Date */}
              <div>
                <label className={labelCls}>Post date *</label>
                <DateField
                  label="Post date"
                  placeholder="Select date"
                  value={postDate}
                  onChange={setPostDate}
                />
                {errors.postDate && <p className="text-xs text-red-500 mt-1">{errors.postDate}</p>}
              </div>

              {/* Tags */}
              <div>
                <label className={labelCls}>Tags (max 5)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    disabled={isPending || tags.length >= 5}
                    placeholder="Type and press Enter…"
                    maxLength={50}
                    className={inputCls(false)}
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    disabled={isPending || !tagInput.trim() || tags.length >= 5}
                    className="px-3 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    Add
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap mt-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-primary/10 text-secondary rounded-full text-xs"
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="text-muted hover:text-secondary transition-colors"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Photos */}
            <div>
              <label className={labelCls}>Photos (max 10)</label>
              <div className="border-2 border-dashed border-divider rounded-lg">
                <label className="flex flex-col items-center justify-center cursor-pointer py-3">
                  <MdUpload className="text-2xl text-muted mb-1" />
                  <p className="text-sm font-medium text-secondary">Drop photos or click to select</p>
                  <p className="text-xs text-subtle mt-0.5">JPEG, PNG, WEBP up to 5MB each</p>
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handlePhotoSelect}
                    disabled={isPending}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Photo previews — horizontal contained strip */}
              {photoPreviews.length > 0 && (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-1 mt-2">
                    {photoPreviews.map((preview, idx) => (
                      <div key={idx} className="relative group w-20 h-20 flex-shrink-0">
                        <img
                          src={preview}
                          alt={`Selection ${idx + 1}`}
                          className="w-full h-full object-cover rounded-lg bg-nav-hover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemovePhoto(idx)}
                          className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center rounded-lg transition opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                        >
                          <MdDelete className="text-white text-xl" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-subtle mt-1">{photos.length}/10 photos</p>
                </>
              )}
            </div>
          </div>

          {/* Sticky footer */}
          <div className="flex items-center gap-3 px-5 py-3.5 border-t border-divider flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={isPending}
              className="flex-1 px-4 py-2 border border-divider rounded-lg text-secondary font-medium hover:bg-nav-hover transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {isPending ? 'Publishing...' : 'Publish Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreatePostModal;
